import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "./Header";
import Right from "./Right";
import { useWebSocket } from "../../WebSocketContext";
import { fetchProfileById } from "../../redux/profileSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyProfile } from "../../redux/myProfileSlice";
import { getUserIdFromToken } from "../../utils/auth";

function Chat() {
  const dispatch = useDispatch();
  const location = useLocation();
  const receiverId = location.state?.profileId;
  const [senderId, setSenderId] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [isChatLimited, setIsChatLimited] = useState(false);

  const scrollRef = useRef(null);

  const { profile } = useSelector((state) => state.myProfile);
  const { sendMessage, subscribe, wsReady } = useWebSocket();

  const FREE_PLAN_ID = "682cdd868ec38b8978f316fb";
  const isFreeUser =
    !profile?.membershipPlan ||
    profile?.membershipPlan?.membershipId === FREE_PLAN_ID;

  // Fetch profile & user id
  useEffect(() => {
    if (!receiverId) return;

    const fetchUserData = async () => {
      const action = await dispatch(fetchProfileById(receiverId));
      setProfileData(action.payload);
      dispatch(fetchMyProfile());
      const uid = await getUserIdFromToken();
      setSenderId(uid);
    };
    fetchUserData();
  }, [receiverId, dispatch]);

  // Handle chat limit for free users
  useEffect(() => {
    const membershipId = profile?.membershipPlan?.membershipId;
    const freeUser = !membershipId || membershipId === FREE_PLAN_ID;

    if (freeUser) {
      setIsChatLimited(messages.length >= 5);
    } else {
      setIsChatLimited(false);
    }
  }, [messages, profile]);

  // Subscribe to websocket messages
  useEffect(() => {
    if (!senderId || !receiverId || !wsReady) return;

    let mounted = true;
    const unsubscribe = subscribe((data) => {
      if (!mounted) return;

      if (data.type === "initialMessages") {
        setMessages(data.messages || []);
      }

      if (data.type === "newMessage" || data.type === "message") {
        const msg =
          typeof data.message === "string"
            ? {
                _id: Date.now().toString(),
                senderId: receiverId,
                receiverId: senderId,
                message: data.message,
              }
            : data.message;

        if (
          (msg.senderId === senderId && msg.receiverId === receiverId) ||
          (msg.senderId === receiverId && msg.receiverId === senderId)
        ) {
          setMessages((prev) => [...prev, msg]);
        }
      }
    });

    sendMessage({
      action: "initialMessages",
      senderId,
      receiverId,
      limit: 50,
      lastMessageId: "",
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [senderId, receiverId, wsReady, sendMessage, subscribe]);

  const handleSend = () => {
    if (isChatLimited) {
      alert("Upgrade Required: Free chat limit reached.");
      return;
    }
    if (!message.trim() || !wsReady) return;

    const newMsg = {
      _id: Date.now().toString(),
      senderId,
      receiverId,
      message: message.trim(),
    };

    sendMessage({ action: "sendMessage", ...newMsg });
    setMessages((prev) => [...prev, newMsg]);
    setMessage("");
  };

  // Auto scroll
  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);

    return () => clearTimeout(timeout);
  }, [messages]);

  return (
    <>
      <Header />
      <section>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-lg-8 col-12 mb-5">
              <div className="bg-white rounded-4">
                <div className="px-4 py-3">
                  {/* Chat Header */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center">
                      <Link to="/message">
                        <i className="bi bi-chevron-left me-3 fs-3"></i>
                      </Link>
                      <img
                        src={
                          profileData?.images?.[0]?.docUrl ||
                          "/assets/images/default-profile.jpg"
                        }
                        className="object-fit-cover rounded-circle"
                        width="50"
                        height="50"
                      />
                      <div className="ms-3">
                        <h4 className="fw-normal ls-sm fw-medium text-dark lh-1 mt-1 mb-0">
                          {profileData?.name || "User"}
                        </h4>
                        <p className="d-flex align-items-center m-0 lh-sm">
                          {profileData?.profile?.city || ""}{" "}
                          <i className="bi bi-circle-fill fs-dot mx-2"></i>
                          {profileData?.profile?.community || ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  <hr />

                  {/* Chat messages */}
                  <div className="vh-70 d-flex flex-column justify-content-between">
                    <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                      {messages.map((msg, i) => {
                        const isSender = msg.senderId === senderId;
                        return (
                          <div
                            key={i}
                            className={`d-flex mb-2 ${
                              isSender
                                ? "justify-content-end"
                                : "justify-content-start"
                            }`}
                          >
                            {!isSender && (
                              <img
                                src={
                                  profileData?.images?.[0]?.docUrl ||
                                  "/assets/images/default-profile.jpg"
                                }
                                className="rounded-circle me-2"
                                width="30"
                                height="30"
                                alt="receiver"
                              />
                            )}
                            <p
                              className={`px-3 py-1 m-0 rounded-3 ${
                                isSender
                                  ? "bg-dark text-white"
                                  : "bg-light-red text-dark"
                              }`}
                            >
                              {msg.message}
                            </p>
                            {isSender && (
                              <img
                                src={
                                  profileData?.images?.[0]?.docUrl ||
                                  "/assets/images/default-profile.jpg"
                                }
                                className="rounded-circle ms-2"
                                width="30"
                                height="30"
                                alt="sender"
                              />
                            )}
                          </div>
                        );
                      })}
                      <div ref={scrollRef} />
                    </div>

                    {/* Input */}
                    {!isChatLimited && (
                      <div className="d-flex position-relative d-flex justify-content-end align-items-center">
                        <input
                          type="text"
                          placeholder="Send Message ..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          disabled={isChatLimited}
                          onKeyDown={(e) => e.key === "Enter" && handleSend()}
                          className="form-control ps-4 pe-8 py-chat-box bg-light border-0 rounded-pill"
                        />
                        <span
                          onClick={handleSend}
                          disabled={isChatLimited}
                          class="icon-shape icon-md ms-1 bg-danger rounded-circle  cursor-pointer position-absolute me-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            class="h-6 rotate-90 text-white"
                          >
                            <path d="M21.68,17.65l-7-14a3,3,0,0,0-5.36,0l-7,14a3,3,0,0,0,3.9,4.08l5.37-2.4h0a1.06,1.06,0,0,1,.82,0l5.37,2.4a3,3,0,0,0,3.9-4.08Zm-2,2a1,1,0,0,1-1.13.22l-5.37-2.39a3,3,0,0,0-2.44,0L5.41,19.9a1,1,0,0,1-1.3-1.35l7-14a1,1,0,0,1,1.78,0l7,14A1,1,0,0,1,19.72,19.68Z"></path>{" "}
                          </svg>
                        </span>
                      </div>
                    )}

                    {isChatLimited && (
                      <div className="alert alert-warning rounded-4 mt-2 text-center">
                        You’ve reached your free chat limit.{" "}
                        <Link to="/upgrade">Upgrade Now</Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Right />
          </div>
        </div>
      </section>
    </>
  );
}

export default Chat;
